import { Flex, Inline, xcss } from '@atlaskit/primitives';
import Select from '@databases/atlaskit/Select';
import ValidationStatus from '@databases/form/ValidationStatus';
import { EntryIconColor } from '@databases/icons';
import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils, useActionWithDependencies } from '@databases/redux';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { useScope } from '@databases/scope';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import { EntryWithContext } from '@databases/types/database';
import {
  DatabaseFieldType,
  RawDatabaseValue,
  DatabaseField,
} from '@databases/types/database';
import { MultiValueChangeType, ValueChange } from '@databases/utils/database/changesets';
import { loadDependencies } from '@databases/utils/database/field-types';
import {
  getDatabaseEntries,
  getDatabaseValue,
  getEntryName,
  getMainField,
} from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdStatus,
} from '@databases/utils/redux/databases/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import { generateId } from '@databases/utils/utils';
import React, { ReactElement, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { getSelectDatabaseDraft } from '../../utils';
import {
  CustomValueContainer,
  baseSelectStyleConfig,
  isRenderedInSidePanelStyling,
} from '../popover-edit-common';

const EntryLinkOptionIconStyles = xcss({
  marginRight: 'space.150',
});

export interface EntryLinkSelectProps {
  value: RawDatabaseValue<DatabaseFieldType.ENTRY_LINK>;
  field: DatabaseField<DatabaseFieldType.ENTRY_LINK>;
  autoFocus?: boolean;
  isClearable?: boolean;
  isRequired?: boolean;
  isLoading?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  onChange: (
    value: RawDatabaseValue<DatabaseFieldType.ENTRY_LINK>,
    change: ValueChange | null
  ) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  isRenderedInSidePanel?: boolean;
}

const validateEntry = (
  value: Array<EntryWithContext>,
  isRequired: boolean | undefined,
  validationMessage: string
): string | undefined => {
  if (isRequired && value.length === 0) {
    return validationMessage;
  }
};

const getOptionValue = (value: EntryWithContext) => (value.entry ? value.entry.id : '');

/**
 * Renders a select box that allows selecting one or multiple (depending on isMulti) specific database entries from the given database.
 */
export function EntryLinkSelect({
  value,
  field,
  autoFocus,
  isClearable,
  isRequired,
  isInvalid,
  onFocus,
  onChange,
  onBlur,
  isRenderedInSidePanel,
}: EntryLinkSelectProps): ReactElement {
  const { multi: isMulti, contentId: databaseContentId } = field.settings;

  const store = defaultStoreUtils.useStore();
  const scope = useScope();
  const intl = useIntl();
  const { formatMessage } = intl;

  // Attempt to load the field's database into the store.
  const dispatch = defaultStoreUtils.useDispatch();
  useCallbackEffect(
    useCallback(async () => {
      try {
        if (databaseContentId) {
          await dispatch(loadDatabaseByContentId(databaseContentId, intl));
        }
        // eslint-disable-next-line canvas/no-swallow-errors-in-catch
      } catch (err) {
        if (err instanceof Error) {
          let errorBody = '';
          const errorMessage = err.message;

          if (errorMessage.includes('NotFoundException')) {
            errorBody = formatMessage(messages['snackbar-error.entry-link-not-found'], {
              fieldName: field.name,
            });
          } else if (errorMessage.includes('PermissionException')) {
            errorBody = formatMessage(
              messages['snackbar-error.entry-link-restricted-permission'],
              {
                fieldName: field.name,
              }
            );
          } else {
            errorBody = formatMessage(messages['snackbar-error.entry-link-load-error'], {
              fieldName: field.name,
            });

            const error = new Error(
              `Could not load database with content id ${databaseContentId}: ${err}`
            );
            logError(error);
          }

          currentSnackbarState.setMessage(errorBody, {
            id: 'database-entry-link-value-edit',
            type: 'error',
            showCloseButton: true,
          });
        }

        return;
      }
    }, [databaseContentId, field.name, formatMessage, dispatch, intl])
  );

  const [isPending, targetDatabaseDraft] = defaultStoreUtils.useShallowEqualSelector(
    (state) => {
      if (databaseContentId) {
        const targetDatabaseStatus = selectDatabaseByContentIdStatus(state, {
          contentId: databaseContentId,
        });
        const targetDatabase = selectDatabaseByContentIdOptional(state, {
          contentId: databaseContentId,
        });
        return [
          targetDatabaseStatus?.status === AsyncOperationStatus.PENDING,
          targetDatabase &&
            selectDatabaseDraftByContentId(state, {
              contentId: databaseContentId,
              scope,
              includeDeleted: false,
            }),
        ] as const;
      } else {
        return [false, undefined];
      }
    }
  );

  const dependencies = useActionWithDependencies(
    useCallback(
      (helpers) => {
        // Load main field dependencies (because the picker item label is the string representation of the main field)
        const mainField = targetDatabaseDraft && getMainField(targetDatabaseDraft);
        if (targetDatabaseDraft && mainField) {
          for (const entry of getDatabaseEntries(targetDatabaseDraft)) {
            const value = getDatabaseValue(targetDatabaseDraft, mainField, entry);
            loadDependencies(
              {
                contentId: targetDatabaseDraft.contentId,
                field: mainField,
                entryId: entry.id,
                value,
              },
              {
                ...helpers,
                selectDatabase: getSelectDatabaseDraft(scope, true),
                intl,
              },
              store.getState()
            );
          }
        }
      },
      [targetDatabaseDraft, scope, intl, store]
    )
  );

  // The currently selected values
  const resolvedValue = useMemo(
    () =>
      targetDatabaseDraft
        ? value.flatMap((v: any): Array<EntryWithContext> => {
            if (targetDatabaseDraft.entriesById[v.entryId]) {
              const entry = targetDatabaseDraft.entriesById[v.entryId];
              return [{ database: targetDatabaseDraft, entry }];
            } else {
              return [];
            }
          })
        : [],
    [targetDatabaseDraft, value]
  );

  // The options available in the select
  const resolvedOptions = useMemo(
    (): Array<EntryWithContext> => [
      ...((targetDatabaseDraft &&
        getDatabaseEntries(targetDatabaseDraft).map((entry) => ({
          database: targetDatabaseDraft,
          entry,
        }))) ??
        []),
    ],
    [targetDatabaseDraft]
  );

  const handleChange = useCallback(
    (passedValue: EntryWithContext | null) => {
      if (!passedValue) {
        return;
      }

      const singleEntryLinkValue = {
        _id: generateId(),
        entryId: passedValue.entry.id,
        title: getEntryName(passedValue, store.getState()),
      };

      onChange([singleEntryLinkValue], {
        type: DatabaseFieldType.ENTRY_LINK,
        delta: {
          type: MultiValueChangeType.APPEND,
          value: singleEntryLinkValue,
        },
      });
    },
    [store, onChange]
  );

  const getOptionLabel = useCallback(
    (value: EntryWithContext) => {
      const entryName = getEntryName(value, store.getState());

      return entryName
        ? entryName
        : formatMessage(messages['unnamed-entities.unnamed-entry']);
    },
    [formatMessage, store]
  );

  const formatOptionLabel = useCallback(
    (value: EntryWithContext): React.ReactNode => {
      const title = getOptionLabel(value);

      return (
        <Flex alignItems="center">
          <Inline xcss={EntryLinkOptionIconStyles}>
            <EntryIconColor label="" />
          </Inline>
          {title}
        </Flex>
      );
    },
    [getOptionLabel]
  );

  return (
    <ValidationStatus
      error={validateEntry(
        resolvedValue,
        isRequired,
        formatMessage(messages['entry-picker.validate-error-text'])
      )}
    >
      {(validationProps) => (
        <Select<EntryWithContext>
          classNamePrefix="entry-picker"
          value={resolvedValue}
          onChange={handleChange}
          isClearable={isClearable}
          isDisabled={isPending ?? !targetDatabaseDraft}
          isInvalid={validationProps.isInvalid || isInvalid}
          formatOptionLabel={formatOptionLabel}
          getOptionLabel={getOptionLabel}
          options={resolvedOptions}
          getOptionValue={getOptionValue}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={autoFocus}
          isLoading={isPending || dependencies.isDispatching}
          noOptionsMessage={() => formatMessage(messages['entry-link.no-options'])}
          placeholder={formatMessage(messages['select-dropdown.placeholder'])}
          controlShouldRenderValue={false}
          isSeamless={true}
          blurInputOnSelect={!isMulti}
          styles={
            isRenderedInSidePanel ? isRenderedInSidePanelStyling : baseSelectStyleConfig
          }
          hideSelectedOptions={true}
          {...(!isRenderedInSidePanel && {
            menuPortalTarget: null,
            defaultMenuIsOpen: true,
            menuIsOpen: true,
            components: {
              IndicatorsContainer: () => null,
              ValueContainer: CustomValueContainer,
            },
          })}
        />
      )}
    </ValidationStatus>
  );
}

export default EntryLinkSelect;
